import { useState } from 'react';
import { groupBy } from 'lodash';
import { type InteractionType } from '@components/Presentation/types';
import { type Theme } from '@components/themes/store/ThemeStore';
import CrawlStage from '@components/Presentation/main/PresentationBody/InteractiveInteractions/CrawlStage';
import useFonts from '@lib/hooks/useFonts';
import { type SAFonts } from '@atoms/FontPicker';

interface CreditsMessage {
  user: string;
  message: string;
}

interface SimpleCreditsProps {
  feature: InteractionType;
  theme: Theme | null;
  startInteraction?: boolean;
  featureColors?: Record<string, any>;
  setting?: Record<string, any>;
  title?: string;
}

const DUMMY_MESSAGES: CreditsMessage[] = [
  { user: 'Alex', message: 'hello' },
  { user: 'Alex', message: 'great session' },
  { user: 'Alex', message: 'love this' },
  { user: 'Sam', message: 'amazing' },
  { user: 'Sam', message: 'wow' },
  { user: 'Jordan', message: 'fantastic' },
  { user: 'Jordan', message: 'so good' },
  { user: 'Taylor', message: 'nice' },
  { user: 'Morgan', message: 'cool' },
  { user: 'Morgan', message: 'great' },
];

export function SimpleCredits({ theme, startInteraction = true, featureColors, setting, title }: SimpleCreditsProps) {
  const [messages] = useState<CreditsMessage[]>(DUMMY_MESSAGES);

  const { getFontStyle } = useFonts();
  const fontFamily = getFontStyle((setting?.FontStyle || theme?.config?.default?.textFontFamily) as SAFonts || 'Sans-Serif').fontFamily;
  const headingFontFamily = getFontStyle((setting?.HeadingFont || theme?.config?.default?.headingFontFamily) as SAFonts || 'Sans-Serif').fontFamily;

  const themeColors = theme?.config?.colors;
  const defaultHeading = theme?.config?.default?.headingColor || '#FFFFFF';

  const textColor = featureColors?.creditsTextColor || themeColors?.creditsTextColor || themeColors?.ffTextColor || defaultHeading;
  const headingColor = featureColors?.creditsHeadingColor || themeColors?.creditsHeadingColor || themeColors?.ffHeadingColor || defaultHeading;

  const sorted = Object.entries(groupBy(messages, 'user'))
    .map(([user, msgs]) => ({ commenterName: user, count: msgs.length }))
    .sort((a, b) => b.count - a.count);

  const topFans = sorted;
  const restFans: typeof sorted = [];

  const creditsTitle = title ?? '';
  const episodeLabel = setting?.creditsEpisodeLabel ?? 'Thank You';
  const topLabel = setting?.creditsTopLabel ?? 'Top Commenters';
  const closingLine = setting?.creditsClosingLine ?? 'See you next time!';
  const speed = Number(setting?.creditsSpeed) || 60;
  const fontSizeScale = (Number(setting?.creditsFontSize) || 1) * 2;
  const showCount = setting?.creditsShowCount !== false;

  return (
    <div className="w-full h-full pt-8 pb-4 z-[2] flex justify-center items-center">
      <CrawlStage
        title={creditsTitle}
        episodeLabel={episodeLabel}
        topLabel={topLabel}
        topFans={topFans}
        restFans={restFans}
        showCount={showCount}
        closingLine={closingLine}
        speed={speed}
        textColor={textColor}
        headingColor={headingColor}
        fontFamily={fontFamily}
        headingFontFamily={headingFontFamily}
        fontSizeScale={fontSizeScale}
      />
    </div>
  );
}
